import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, clearSession, errText, session, setOnLogout, storeSession } from './api.js';
import { resolveLang, storeLang, makeT } from './i18n.js';
import { LangContext } from './lang.js';
import Shell from './Shell.jsx';
import Assistant from './Assistant.jsx';
import Alerts from './Alerts.jsx';
import { closeRealtime } from './realtime.js';
import Login from './pages/Login.jsx';
import Overview from './pages/Overview.jsx';
import Settings from './pages/Settings.jsx';
import SettingsShell from './pages/SettingsShell.jsx';
import Staff from './pages/Staff.jsx';
import Menu from './pages/Menu.jsx';
import Tables from './pages/Tables.jsx';
import OrderEntry from './pages/OrderEntry.jsx';
import Bill from './pages/Bill.jsx';
import Printers from './pages/Printers.jsx';
import PaymentAccounts from './pages/PaymentAccounts.jsx';
import Loyalty from './pages/Loyalty.jsx';
import Conflicts from './pages/Conflicts.jsx';
import QrSheet from './pages/QrSheet.jsx';
import Kitchen from './pages/Kitchen.jsx';
import Counter from './pages/Counter.jsx';
import Payments from './pages/Payments.jsx';
import Billing from './pages/Billing.jsx';
import AdminLogin from './pages/AdminLogin.jsx';
import AdminRestaurants from './pages/AdminRestaurants.jsx';
import AdminPlans from './pages/AdminPlans.jsx';
import AdminCountries from './pages/AdminCountries.jsx';

export default function App() {
  // null = henüz bilinmiyor (yenileme deneniyor), false = oturum yok
  const [me, setMe] = useState(null);
  const [kind, setKind] = useState(session().kind);
  // Panelin dili PERSONELE ait: cihazda saklanır, restoranın `locale`'i
  // yalnız ilk tahmindir (bkz. i18n.js).
  const [lang, setLang] = useState(() => resolveLang(null));
  const pickLang = useCallback((code) => { storeLang(code); setLang(code); }, []);
  const t = makeT(lang);
  const nav = useNavigate();

  const logout = useCallback(() => {
    closeRealtime();
    clearSession();
    setMe(false);
    setKind(null);
  }, []);

  useEffect(() => { setOnLogout(logout); }, [logout]);

  // TEST ödeme kipi üst çubukta yazsın: açık kaldığını kimse unutmasın.
  // Ayar ekranı değiştirince olayı yayınlar (sayfa yenilemeye gerek yok).
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => {
    if (!me?.staff) { setDemoMode(false); return; }
    const read = () => api('/api/restaurant')
      .then((d) => setDemoMode(!!d.settings?.demoPayments)).catch(() => {});
    read();
    window.addEventListener('orderceo:settings-changed', read);
    return () => window.removeEventListener('orderceo:settings-changed', read);
  }, [me]);

  // Açılışta: refresh token varsa oturumu geri kur (sayfa yenilemesinde
  // kullanıcıyı dışarı atmamak için).
  useEffect(() => {
    (async () => {
      const rt = localStorage.getItem('orderceo.refresh');
      if (!rt) return setMe(false);
      try {
        const data = await api('/api/auth/refresh', { method: 'POST', body: { refreshToken: rt }, retry: false });
        storeSession({ accessToken: data.accessToken, refreshToken: data.refreshToken, kind: data.admin ? 'platform' : 'staff' });
        setKind(data.admin ? 'platform' : 'staff');
        setMe(data.admin ? { admin: data.admin } : { staff: data.staff, restaurant: data.restaurant });
        if (data.restaurant?.locale) setLang(resolveLang(data.restaurant.locale));
      } catch {
        clearSession();
        setMe(false);
      }
    })();
  }, []);

  // <html lang>: ekran okuyucu ve tarayıcının çeviri önerisi bunu okur.
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  if (me === null) return <div className="center"><span className="muted">{t('loading')}</span></div>;

  if (!me) {
    return (
      <LangContext.Provider value={lang}>
        <Routes>
          <Route path="/admin/*" element={<AdminLogin onDone={(d) => { setKind('platform'); setMe({ admin: d.admin }); nav('/admin'); }} />} />
          <Route path="*" element={
            <Login lang={lang} onPickLang={pickLang}
              onDone={(d) => {
                setKind('staff');
                setMe({ staff: d.staff, restaurant: d.restaurant });
                if (d.restaurant?.locale) setLang(resolveLang(d.restaurant.locale));
                nav('/');
              }} />
          } />
        </Routes>
      </LangContext.Provider>
    );
  }

  if (kind === 'platform') {
    // Süper admin ekranları çevrilmedi (bkz. docs/I18N.md §5) ama kabuk
    // ortak: telefonda o ekranlar da aynı dertten muzdaripti.
    return (
      <Shell
        nav={[['/admin', 'Restoranlar', true, 'store'], ['/admin/plans', 'Paketler', false, 'tag'],
              ['/admin/countries', 'Ülkeler', false, 'globe']]}
        user={{ name: me.admin?.email, email: me.admin?.email, subtitle: 'Süper admin' }}
        lang={lang} onPickLang={pickLang} onLogout={logout} t={t}
      >
        <Routes>
          <Route path="/admin" element={<AdminRestaurants />} />
          <Route path="/admin/plans" element={<AdminPlans />} />
          <Route path="/admin/countries" element={<AdminCountries />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Shell>
    );
  }

  // Tek liste: etiket çevirisi ve sıra tek yerde durur.
  // Yalnız günlük işte açılan ekranlar; kurulum ekranları «Ayarlar»ın
  // sekmelerinde (pages/SettingsShell.jsx).
  // Dördüncü alan = simge adı (Icon.jsx): etiketle aynı satırda dursun ki
  // biri eklenip öteki unutulmasın.
  // «Genel» gezinmeden ÇIKTI: içeriği restoran künyesi + paket yetenekleri,
  // yani günlük işte açılan bir ekran değil — Ayarlar'ın bir sekmesine indi
  // (pages/SettingsShell.jsx) ve ana yol artık Salon.
  const NAV = [
    ['/tables', 'nav.tables', false, 'grid'],
    ['/counter', 'nav.counter', false, 'keypad'],
    ['/menu', 'nav.menu', false, 'book'], ['/kitchen', 'nav.kitchen', false, 'chef'],
    ['/payments', 'nav.payments', false, 'card'], ['/settings', 'nav.settings', false, 'gear'],
  ];

  return (
    <LangContext.Provider value={lang}>
      <Shell
        nav={NAV.map(([to, key, end, icon]) => [to, t(key), end, icon])}
        user={{
          name: me.staff?.name,
          email: me.staff?.email,
          avatarUrl: me.staff?.avatarUrl,
          subtitle: `${me.restaurant?.name} · ${t(`role.${me.staff?.role}`)}`,
          // Ücretli pakete geçilmediyse üst çubukta yükseltme çağrısı durur.
          // Durum oturum açılışındaki paketten okunur; abonelik Stripe'tan
          // dönünce sayfa yenilendiği için çağrı kendiliğinden kaybolur.
          upgrade: me.restaurant?.subscriptionStatus !== 'active',
        }}
        demo={demoMode}
        lang={lang} onPickLang={pickLang} onLogout={logout} t={t}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/tables" replace />} />
          <Route path="/tables" element={<Tables />} />
          {/* Garsonun sipariş ekranı: masadan tek dokunuşla açılır. */}
          <Route path="/order/:tableId" element={<OrderEntry />} />
          <Route path="/sessions/:id" element={<Bill />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/qr" element={<QrSheet />} />
          <Route path="/kitchen" element={<Kitchen />} />
          {/* Perakende (tezgah) satış: masasız müşteri, tek ekranda tahsilat. */}
          <Route path="/counter" element={<Counter />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/settings" element={<SettingsShell />}>
            <Route index element={<Settings />} />
            <Route path="overview" element={<Overview />} />
            <Route path="staff" element={<Staff />} />
            <Route path="accounts" element={<PaymentAccounts />} />
            <Route path="printers" element={<Printers />} />
            <Route path="loyalty" element={<Loyalty />} />
            <Route path="devices" element={<Conflicts />} />
            <Route path="billing" element={<Billing />} />
          </Route>
          {/* Eski yollar: yer imleri ve dışarıdaki bağlantılar kırılmasın. */}
          <Route path="/staff" element={<Navigate to="/settings/staff" replace />} />
          <Route path="/printers" element={<Navigate to="/settings/printers" replace />} />
          <Route path="/loyalty" element={<Navigate to="/settings/loyalty" replace />} />
          <Route path="/sync" element={<Navigate to="/settings/devices" replace />} />
          <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
          <Route path="*" element={<Navigate to="/tables" replace />} />
        </Routes>
        {/* Asistan yalnız personel kabuğunda: süper adminin kiracı verisine
            araç üzerinden erişmesi istenmiyor. */}
        <Assistant />
        {/* Servis uyarıları (yeni sipariş / hesap istendi) her ekranda. */}
        <Alerts t={t} />
      </Shell>
    </LangContext.Provider>
  );
}

export { errText };
